import { useState } from "react";
import { Button } from "@/components/ui/button";
import { TrendingUp, BarChart3, PieChart, ShoppingCart, Rocket, ChevronRight, ChevronLeft, DollarSign, AlertTriangle } from "lucide-react";

interface Props {
  onComplete: () => void;
}

const STEPS = [
  {
    icon: Rocket,
    title: "Welcome to the Market Simulator",
    body: "You're about to enter a realistic stock market simulation. You'll invest in fictional companies, react to market news, and learn how real investing works — all without risking real money.",
    color: "text-primary",
  },
  {
    icon: BarChart3,
    title: "Your Dashboard",
    body: "Your dashboard shows your portfolio value, cash balance, and a list of stocks you can buy or sell. Watch stock prices change each round as market conditions shift. Use the Buy and Sell buttons to make trades.",
    color: "text-accent",
  },
  {
    icon: TrendingUp,
    title: "How Markets Work",
    body: "Stock prices move based on market cycles (expansion, recession, recovery), news events, and overall sentiment. Volatility means prices can swing up or down. Understanding these patterns is key to smart investing.",
    color: "text-emerald-400",
  },
  {
    icon: PieChart,
    title: "Diversification & Strategy",
    body: "Don't put all your money in one stock! Spreading investments across different sectors reduces risk. Long-term investors who stay calm during downturns historically outperform those who panic sell.",
    color: "text-amber-400",
  },
  {
    icon: ShoppingCart,
    title: "Make Your First Trade",
    body: "When the simulator starts, review the available stocks, check their sectors and price trends, then buy your first shares. Each round represents a time period — advance rounds to see how your investments perform!",
    color: "text-secondary",
  },
];

// Practice round mock data
const PRACTICE_STOCKS = [
  { ticker: "DEMO", name: "DemoTech Inc.", price: 50, sector: "Technology" },
  { ticker: "SAFE", name: "SafeGrow Corp.", price: 30, sector: "Consumer Goods" },
  { ticker: "RISK", name: "RiskVenture Ltd.", price: 80, sector: "Energy" },
];

type PracticePhase = "intro" | "buy" | "news" | "result" | "done";

function PracticeRound({ onComplete }: { onComplete: () => void }) {
  const [phase, setPhase] = useState<PracticePhase>("intro");
  const [cash, setCash] = useState(1000);
  const [holdings, setHoldings] = useState<Record<string, number>>({});
  const [priceChanges, setPriceChanges] = useState<Record<string, number>>({});

  const buyStock = (ticker: string, price: number) => {
    if (cash < price) return;
    setCash(c => c - price);
    setHoldings(h => ({ ...h, [ticker]: (h[ticker] || 0) + 1 }));
  };

  const simulateRound = () => {
    const changes: Record<string, number> = {
      DEMO: Math.round((Math.random() * 20 - 5) * 100) / 100,
      SAFE: Math.round((Math.random() * 10 - 2) * 100) / 100,
      RISK: Math.round((Math.random() * 40 - 15) * 100) / 100,
    };
    setPriceChanges(changes);
    setPhase("result");
  };

  const totalShares = Object.values(holdings).reduce((a, b) => a + b, 0);
  const portfolioValue = PRACTICE_STOCKS.reduce((sum, s) => {
    const change = priceChanges[s.ticker] || 0;
    return sum + (holdings[s.ticker] || 0) * (s.price + change);
  }, 0);

  if (phase === "intro") {
    return (
      <div className="space-y-6 text-center">
        <div className="w-16 h-16 rounded-2xl bg-muted/30 border border-border flex items-center justify-center mx-auto">
          <DollarSign className="w-8 h-8 text-accent" />
        </div>
        <h2 className="font-heading font-bold text-2xl text-foreground">Practice Round</h2>
        <p className="text-muted-foreground text-sm leading-relaxed max-w-md mx-auto">
          You have <span className="text-primary font-bold">$1,000</span> in practice money. 
          Buy some stocks below to see how investing works. This is just practice — no real impact on your account!
        </p>
        <Button onClick={() => setPhase("buy")} className="bg-primary hover:bg-primary/90">
          Start Practice <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    );
  }

  if (phase === "buy") {
    return (
      <div className="space-y-5">
        <div className="text-center space-y-1">
          <h2 className="font-heading font-bold text-lg text-foreground">Buy Some Stocks</h2>
          <p className="text-sm text-muted-foreground">Cash: <span className="text-primary font-bold">${cash.toFixed(0)}</span></p>
        </div>

        <div className="space-y-3">
          {PRACTICE_STOCKS.map(stock => (
            <div key={stock.ticker} className="glass-card p-4 flex items-center justify-between">
              <div>
                <p className="font-heading font-bold text-foreground">{stock.ticker}</p>
                <p className="text-xs text-muted-foreground">{stock.name} • {stock.sector}</p>
                <p className="text-sm font-semibold text-primary">${stock.price}</p>
              </div>
              <div className="flex items-center gap-2">
                {(holdings[stock.ticker] || 0) > 0 && (
                  <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                    Owns: {holdings[stock.ticker]}
                  </span>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  disabled={cash < stock.price}
                  onClick={() => buyStock(stock.ticker, stock.price)}
                >
                  Buy 1
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center space-y-3">
          <p className="text-xs text-muted-foreground">
            {totalShares === 0 ? "Buy at least one share to continue" : `You own ${totalShares} share${totalShares > 1 ? 's' : ''}`}
          </p>
          <Button
            disabled={totalShares === 0}
            onClick={() => setPhase("news")}
            className="bg-primary hover:bg-primary/90"
          >
            Advance Round <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    );
  }

  if (phase === "news") {
    return (
      <div className="space-y-6 text-center">
        <div className="w-16 h-16 rounded-2xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mx-auto">
          <AlertTriangle className="w-8 h-8 text-amber-400" />
        </div>
        <h2 className="font-heading font-bold text-xl text-foreground">Breaking News!</h2>
        <div className="glass-card p-4 text-left space-y-2">
          <p className="text-sm text-foreground font-medium">📰 "Tech sector sees mixed earnings reports while energy prices fluctuate on global demand uncertainty."</p>
          <p className="text-xs text-muted-foreground">
            News events affect stock prices. In the real simulator, you'll see how news impacts different sectors and decide whether to buy, sell, or hold.
          </p>
        </div>
        <Button onClick={simulateRound} className="bg-primary hover:bg-primary/90">
          See Results <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    );
  }

  if (phase === "result") {
    const totalGainLoss = PRACTICE_STOCKS.reduce((sum, s) => {
      return sum + (holdings[s.ticker] || 0) * (priceChanges[s.ticker] || 0);
    }, 0);
    const isPositive = totalGainLoss >= 0;

    return (
      <div className="space-y-5">
        <div className="text-center space-y-2">
          <h2 className="font-heading font-bold text-xl text-foreground">Round Results</h2>
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold ${isPositive ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
            {isPositive ? '📈' : '📉'} {isPositive ? '+' : ''}{totalGainLoss.toFixed(2)}
          </div>
        </div>

        <div className="space-y-2">
          {PRACTICE_STOCKS.map(stock => {
            const shares = holdings[stock.ticker] || 0;
            if (shares === 0) return null;
            const change = priceChanges[stock.ticker] || 0;
            const newPrice = stock.price + change;
            return (
              <div key={stock.ticker} className="glass-card p-3 flex items-center justify-between text-sm">
                <span className="font-heading font-bold text-foreground">{stock.ticker}</span>
                <span className="text-muted-foreground">{shares} share{shares > 1 ? 's' : ''}</span>
                <span className={change >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                  ${stock.price} → ${newPrice.toFixed(2)} ({change >= 0 ? '+' : ''}{change.toFixed(2)})
                </span>
              </div>
            );
          })}
        </div>

        <div className="glass-card p-4 text-center space-y-2">
          <p className="text-sm text-muted-foreground">Portfolio Value</p>
          <p className="text-2xl font-heading font-bold text-foreground">${(cash + portfolioValue).toFixed(0)}</p>
          <p className="text-xs text-muted-foreground">
            {isPositive
              ? "Nice! Your stocks went up. In real markets, patience and diversification pay off."
              : "Some stocks dropped. This is normal! Long-term investors ride out dips instead of panic selling."}
          </p>
        </div>

        <Button onClick={() => setPhase("done")} className="w-full bg-primary hover:bg-primary/90">
          Continue <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    );
  }

  // done
  return (
    <div className="space-y-6 text-center">
      <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 border border-emerald-400/30 flex items-center justify-center mx-auto">
        <TrendingUp className="w-8 h-8 text-emerald-400" />
      </div>
      <h2 className="font-heading font-bold text-2xl text-foreground">Practice Complete!</h2>
      <p className="text-muted-foreground text-sm leading-relaxed max-w-md mx-auto">
        You've learned the basics of buying stocks, reacting to news, and reading results. 
        Now you're ready for the real simulator with 20 rounds, 25 stocks across 5 sectors, and dynamic market events.
      </p>
      <div className="glass-card p-4 text-left space-y-1 text-sm">
        <p className="font-heading font-bold text-foreground mb-2">Key Takeaways:</p>
        <p className="text-muted-foreground">✅ Diversify across sectors to reduce risk</p>
        <p className="text-muted-foreground">✅ Don't panic sell during downturns</p>
        <p className="text-muted-foreground">✅ Watch news events for opportunities</p>
        <p className="text-muted-foreground">✅ Think long-term, not round-to-round</p>
      </div>
      <Button onClick={onComplete} className="bg-primary hover:bg-primary/90">
        Start Real Simulator! <Rocket className="w-4 h-4 ml-1" />
      </Button>
    </div>
  );
}

export default function SimTutorial({ onComplete }: Props) {
  const [step, setStep] = useState(0);
  const [showPractice, setShowPractice] = useState(false);
  const isLast = step === STEPS.length - 1;

  if (showPractice) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="max-w-lg w-full">
          <PracticeRound onComplete={onComplete} />
        </div>
      </div>
    );
  }

  const current = STEPS[step];
  const Icon = current.icon;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="max-w-lg w-full space-y-6">
        {/* Progress dots */}
        <div className="flex justify-center gap-2">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                i === step ? "bg-primary w-6" : i < step ? "bg-primary/50" : "bg-muted"
              }`}
            />
          ))}
        </div>

        <div className="glass-card glow-border p-8 space-y-6 text-center animate-scale-in">
          <div className="w-16 h-16 rounded-2xl bg-muted/30 border border-border flex items-center justify-center mx-auto">
            <Icon className={`w-8 h-8 ${current.color}`} />
          </div>

          <div className="space-y-3">
            <h2 className="font-heading font-bold text-2xl text-foreground">{current.title}</h2>
            <p className="text-muted-foreground font-body leading-relaxed text-sm">{current.body}</p>
          </div>

          <div className="flex gap-3 justify-center pt-2">
            {step > 0 && (
              <Button
                variant="outline"
                className="border-border text-muted-foreground hover:bg-muted/30"
                onClick={() => setStep(s => s - 1)}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            )}
            <Button
              className="bg-primary hover:bg-primary/90"
              onClick={() => {
                if (isLast) {
                  setShowPractice(true);
                } else {
                  setStep(s => s + 1);
                }
              }}
            >
              {isLast ? "Try Practice Round" : "Next"}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>

          {isLast && (
            <button
              onClick={onComplete}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Skip practice → Start investing directly
            </button>
          )}
        </div>

        <p className="text-center text-muted-foreground/50 text-xs">
          Step {step + 1} of {STEPS.length}
        </p>
      </div>
    </div>
  );
}
